import { useState } from 'react'
import { Button } from '@/common/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { ScrollArea, ScrollBar } from '@/common/ui/scroll-area'
import { Clock } from 'lucide-react'
import { cn } from '@/common/lib/utils'

interface TimePickerProps {
	value: string
	onChange: (value: string) => void
	disabled?: boolean
	placeholder?: string
	className?: string
}

export const TimePicker = ({
	value,
	onChange,
	disabled,
	placeholder = 'เลือกเวลา',
	className,
}: TimePickerProps) => {
	const [isOpen, setIsOpen] = useState(false)

	// Parse current time value (HH:mm format)
	const [hours, minutes] = value ? value.split(':') : ['', '']

	// Generate hours (00-23) and minutes (00-59) arrays
	const hoursArray = Array.from({ length: 24 }, (_, i) =>
		i.toString().padStart(2, '0'),
	)
	const minutesArray = Array.from({ length: 60 }, (_, i) =>
		i.toString().padStart(2, '0'),
	)

	const handleTimeChange = (type: 'hour' | 'minute', val: string) => {
		const currentHour = hours || '00'
		const currentMinute = minutes || '00'
		const newTime =
			type === 'hour' ? `${val}:${currentMinute}` : `${currentHour}:${val}`
		onChange(newTime)
	}

	const displayValue = value || placeholder

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					disabled={disabled}
					className={cn(
						'w-full justify-start text-left font-normal',
						!value && 'text-muted-foreground',
						className,
					)}
				>
					<Clock className="mr-2 h-4 w-4" />
					{displayValue}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<div className="flex divide-x">
					<div className="flex flex-col">
						<div className="border-b px-3 py-2 text-center text-sm font-medium">
							ชั่วโมง
						</div>
						<ScrollArea className="h-[200px]">
							<div className="flex flex-col p-2">
								{hoursArray.map((hour) => (
									<Button
										key={hour}
										type="button"
										size="sm"
										variant={hours === hour ? 'default' : 'ghost'}
										className="shrink-0 justify-center"
										onClick={() => handleTimeChange('hour', hour)}
									>
										{hour}
									</Button>
								))}
							</div>
							<ScrollBar orientation="vertical" />
						</ScrollArea>
					</div>
					<div className="flex flex-col">
						<div className="border-b px-3 py-2 text-center text-sm font-medium">
							นาที
						</div>
						<ScrollArea className="h-[200px]">
							<div className="flex flex-col p-2">
								{minutesArray.map((minute) => (
									<Button
										key={minute}
										type="button"
										size="sm"
										variant={minutes === minute ? 'default' : 'ghost'}
										className="shrink-0 justify-center"
										onClick={() => handleTimeChange('minute', minute)}
									>
										{minute}
									</Button>
								))}
							</div>
							<ScrollBar orientation="vertical" />
						</ScrollArea>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	)
}
